import { omit } from 'lodash-es';
import { useCallback, useState } from 'react';
import { useLoadingBarState } from '../../../components/LoadingBar/LoadingBarContext';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import {
  CompanyMappingAction,
  CompanyMappingPayload,
  CompanyMappingVM,
  foresightCompanyId,
} from '../../../schemas/CompanyMappings.schema';
import { updateCompanyMapping } from '../../../services/queries/MaggieCompanyQueries';
import { getErrorMessage } from '../../../services/queryHelpers';
import { LoadingId } from '../../../types';
import { useInvalidateMappingsAndUpdateGrid } from './useInvalidateMappingsAndUpdateGrid';

export function useUpdateCompanyMappings() {
  const {
    actions: { startLoading, stopLoading },
  } = useLoadingBarState();
  const { pushErrorToast } = useToastMessageState();
  const refetchMappingsAndUpdateGrid = useInvalidateMappingsAndUpdateGrid();
  const [isUpdating, setIsUpdating] = useState(false);

  // The endpoint returns 200 even when rows are refused, so read `failures`: failed rows are left
  // out of the refresh (they stay modified) and named in an error toast. Resolves true only if all saved.
  const saveMappings = useCallback(
    async (rows: CompanyMappingVM[], action: CompanyMappingAction): Promise<boolean> => {
      const payload = rows.map((row) => prepareMappingPayload(row, action));
      const { failures = [] } = await updateCompanyMapping(payload);
      const failedIds = new Set(failures.map((failure) => failure.itemId));
      const savedIds = new Set(payload.map((item) => item.id).filter((id) => !failedIds.has(id)));
      if (savedIds.size > 0) {
        await refetchMappingsAndUpdateGrid(savedIds);
      }
      if (failures.length === 0) {
        return true;
      }
      // A row can fail more than once (e.g. claim abort, then revert save), so group reasons per row.
      const reasonsById = new Map<string, string[]>();
      failures.forEach(({ itemId, reason }) =>
        reasonsById.set(itemId, [...(reasonsById.get(itemId) ?? []), reason])
      );
      const namesById = new Map(rows.map((row) => [row.id, row.providerCompanyName]));
      const details = [...reasonsById].map(
        ([itemId, reasons]) => `${namesById.get(itemId) || itemId}: ${reasons.join('; ')}`
      );
      // Cap the list: the toast has a fixed width and no max height.
      const more = details.length > 3 ? `. And ${details.length - 3} more` : '';
      pushErrorToast({ message: `Failed to save. ${details.slice(0, 3).join('. ')}${more}` });
      return false;
    },
    [pushErrorToast, refetchMappingsAndUpdateGrid]
  );

  const createMappings = useCallback(
    async (data: CompanyMappingVM[]): Promise<boolean> => {
      setIsUpdating(true);
      startLoading(LoadingId.updateCompanyMappings);
      try {
        return await saveMappings(data, CompanyMappingAction.create);
      } catch (err) {
        console.error(err);
        pushErrorToast({ message: getErrorMessage(err, 'Failed to save mappings') });
        return false;
      } finally {
        stopLoading(LoadingId.updateCompanyMappings);
        setIsUpdating(false);
      }
    },
    [pushErrorToast, saveMappings, startLoading, stopLoading]
  );

  const skipMapping = useCallback(
    async (data: CompanyMappingVM) => {
      setIsUpdating(true);
      startLoading(LoadingId.updateCompanyMappings);
      try {
        await saveMappings([data], CompanyMappingAction.skip);
      } catch (err) {
        console.error(err);
        pushErrorToast({ message: getErrorMessage(err, 'Failed to skip mapping') });
      } finally {
        stopLoading(LoadingId.updateCompanyMappings);
        setIsUpdating(false);
      }
    },
    [pushErrorToast, saveMappings, startLoading, stopLoading]
  );

  return {
    createMappings,
    skipMapping,
    isUpdating,
  };
}

function prepareMappingPayload(data: CompanyMappingVM, action: CompanyMappingAction): CompanyMappingPayload {
  const payload = omit(data, ['company', 'status', 'modified']);
  return {
    ...payload,
    companyId: foresightCompanyId(data.company),
    companyName: data.company.fields.name,
    companySite: data.company.fields.website,
    action,
  } as CompanyMappingPayload;
}
